import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Loader2, Check, Sparkles, Zap, Coins, Crown, ArrowRight, Star, ShieldCheck, Lock, QrCode } from "lucide-react";
import { QRPricingCards } from "@/components/pricing/QRPricingCards";

const fmt = (cents) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    api.get("/payments/catalog").then((r) => setCatalog(r.data)).catch(() => toast.error("Couldn't load pricing.")).finally(() => setLoading(false));
  }, []);

  const purchase = async (lookup_key) => {
    if (!user) {
      toast.info("Sign in first — it's one tap.");
      const redirectUrl = window.location.origin + "/pricing";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    setBuying(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBuying(null);
    }
  };

  if (loading || !catalog) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#F2C94C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] glyph-bg">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-14">
        {/* Hero */}
        <div className="text-center mb-14 action-vignette action-stripes rounded-3xl p-8 md:p-12 relative overflow-hidden">
          <div className="absolute top-8 left-8 w-1 h-24 bg-gradient-to-b from-transparent via-[#F2C94C] to-transparent" />
          <div className="absolute bottom-8 right-8 w-24 h-1 bg-gradient-to-r from-transparent via-[#EB5757] to-transparent" />
          <div className="inline-flex items-center gap-2 font-mono-hy text-[10px] text-[#F2C94C] mb-4 px-3 py-1 rounded-full bg-[#F2C94C]/10 border border-[#F2C94C]/30">
            <Sparkles className="w-3 h-3" /> PRICING · CREDITS
          </div>
          <h1 className="font-display font-bold text-5xl md:text-6xl tracking-tight mb-4">
            Fuel the <span className="bg-gradient-to-r from-[#F2C94C] via-[#E27D60] to-[#00E5FF] bg-clip-text text-transparent">Afro Sci-Fi story</span>
          </h1>
          <span className="streak mb-4 inline-block" />
          <p className="font-body text-[#D9D2BE] text-lg max-w-2xl mx-auto">
            Subscribe monthly for the fastest queue, or top up with credit packs. Every new creator starts with <span className="text-[#F2C94C] font-semibold">{catalog.free_tier} free credits</span>.
          </p>
          {/* Secured-by-Stripe trust row */}
          <div
            data-testid="stripe-trust-badge"
            className="mt-6 inline-flex items-center gap-3 px-4 py-2 rounded-full bg-[#0A0A0A]/60 border border-[#F2C94C]/15 backdrop-blur-sm"
          >
            <ShieldCheck className="w-3.5 h-3.5 text-[#00E5FF]" />
            <span className="font-mono-hy text-[10px] tracking-widest text-white">
              SECURED BY <span className="text-white font-bold">STRIPE</span>
            </span>
            <span className="w-px h-3 bg-[#F2C94C]/20" />
            <Lock className="w-3 h-3 text-[#F2C94C]" />
            <span className="font-mono-hy text-[10px] tracking-widest text-white">256-BIT SSL</span>
          </div>
        </div>

        {/* Subscriptions */}
        <section className="mb-16" data-testid="subscriptions-section">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="font-display text-2xl md:text-3xl text-white">Monthly Plans</h2>
            <span className="font-mono-hy text-[10px] text-white">Cancel anytime · in Stripe portal</span>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {catalog.subscriptions.map((s, i) => {
              const highlight = i === 1;
              const Icon = i === 0 ? Zap : Crown;
              return (
                <div
                  key={s.lookup_key}
                  data-testid={`plan-${s.lookup_key}`}
                  className={`relative p-7 rounded-3xl border transition-all hover:-translate-y-1 ${
                    highlight
                      ? "bg-gradient-to-br from-[#1a1208] via-[#120E15] to-[#0d1f24] border-[#F2C94C] shadow-[0_0_60px_-15px_rgba(242,201,76,0.3)]"
                      : "bg-[#120E15] border-[#F2C94C]/20"
                  }`}
                >
                  {highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 font-mono-hy text-[10px] px-3 py-1 rounded-full bg-[#F2C94C] text-[#0A0A0A] font-bold">
                      MOST POWER
                    </div>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${highlight ? "bg-[#F2C94C] text-[#0A0A0A]" : "bg-[#F2C94C]/15 text-[#F2C94C]"}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <div>
                      <div className="font-display text-2xl text-white">{s.name}</div>
                      <div className="font-mono-hy text-[10px] text-white">{s.credits_per_month} credits / month</div>
                    </div>
                  </div>
                  <div className="mb-5 flex items-baseline gap-1">
                    <span className="font-display text-5xl text-white font-bold">{fmt(s.amount)}</span>
                    <span className="font-body text-white">/ month</span>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 font-body text-sm text-white">
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${highlight ? "text-[#F2C94C]" : "text-[#00E5FF]"}`} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => purchase(s.lookup_key)}
                    disabled={buying === s.lookup_key}
                    data-testid={`buy-${s.lookup_key}-btn`}
                    className={`w-full font-mono-hy text-xs px-6 py-4 rounded-2xl transition-all flex items-center justify-center gap-2 ${
                      highlight
                        ? "bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] glow-gold"
                        : "border-2 border-[#F2C94C]/40 text-[#F2C94C] hover:bg-[#F2C94C]/10 hover:border-[#F2C94C]"
                    } disabled:opacity-60`}
                  >
                    {buying === s.lookup_key ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Redirecting…</> : <>Subscribe <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* Credit packs */}
        <section className="mb-16" data-testid="creditpacks-section">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="font-display text-2xl md:text-3xl text-white">Credit Packs</h2>
            <span className="font-mono-hy text-[10px] text-white">One-time · never expire</span>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {catalog.credit_packs.map((p) => {
              const perCredit = (p.amount / 100) / p.credits;
              return (
                <div
                  key={p.lookup_key}
                  data-testid={`pack-${p.lookup_key}`}
                  className="relative p-6 rounded-2xl bg-[#120E15] border border-[#F2C94C]/15 hover:border-[#F2C94C]/50 transition-all hover:-translate-y-0.5"
                >
                  {p.badge && (
                    <div className="absolute -top-2 right-4 font-mono-hy text-[9px] px-2 py-1 rounded-full bg-[#00E5FF] text-[#0A0A0A] font-bold flex items-center gap-1">
                      <Star className="w-2.5 h-2.5 fill-current" /> {p.badge.toUpperCase()}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-3">
                    <Coins className="w-5 h-5 text-[#F2C94C]" />
                    <div className="font-display text-lg text-white">{p.name}</div>
                  </div>
                  <div className="font-display text-4xl text-white font-bold mb-1">{p.credits.toLocaleString()}</div>
                  <div className="font-mono-hy text-[10px] text-white mb-4">credits · ${perCredit.toFixed(3)}/credit</div>
                  <div className="mb-4 font-body text-2xl text-[#F2C94C] font-semibold">{fmt(p.amount)}</div>
                  <button
                    onClick={() => purchase(p.lookup_key)}
                    disabled={buying === p.lookup_key}
                    data-testid={`buy-${p.lookup_key}-btn`}
                    className="w-full font-mono-hy text-xs px-5 py-3 rounded-xl border border-[#F2C94C]/40 text-[#F2C94C] hover:bg-[#F2C94C]/10 hover:border-[#F2C94C] transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {buying === p.lookup_key ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Redirecting…</> : <>Buy Pack <ArrowRight className="w-3.5 h-3.5" /></>}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* ── Shared QR pricing cards (Custom QR + Dynamic QR) ─── */}
        <QRPricingCards />

        <section className="p-6 md:p-8 rounded-3xl bg-[#120E15] border border-[#F2C94C]/15" data-testid="costs-section">
          <h3 className="font-display text-xl text-white mb-4">What credits unlock</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(catalog.costs).map(([action, cost]) => (
              <div key={action} className="p-4 rounded-xl bg-[#0A0A0A] border border-[#F2C94C]/10 flex items-center justify-between">
                <span className="font-body text-sm text-white capitalize">{action.replace("-", " ")}</span>
                <span className="font-mono-hy text-[11px] text-[#F2C94C] font-bold">{cost} cr</span>
              </div>
            ))}
          </div>
        </section>

        {/* Tax footnote */}
        <p className="mt-8 text-center font-mono-hy text-[10px] text-white max-w-2xl mx-auto flex items-center justify-center gap-2 flex-wrap">
          <Lock className="w-3 h-3 text-[#F2C94C]" />
          <span>Payments processed securely by <span className="text-white font-bold">Stripe</span>. Tax &amp; compliance handled in ~80 countries. Cards are never seen or stored by Geneo AI.</span>
        </p>
      </div>
      <Footer />
    </div>
  );
}
